import type { GroupsInfo, LlmProviderInfo, LogAction, Student } from '@monorepo/golab-generated-models'

export const waitingForInitChannelMessaging = 'waitingForInitChannelMessaging'
export const logActionMessageKey = 'ACTION'
export const storageHandlerMessageKey = 'STORAGE'
export const logAccessorMessageKey = 'LOG_ACCESSOR'
export const userManagerMessageKey = 'USER_MANAGER'
export const resizeIFrameMessageKey = 'ResizeIframe'
export const callChatGptKey = 'CallCatGPT'
export const llmManagerMessageKey = 'LLM_MANAGER'
export const callChatLlmKey = 'CallChatLLM'
export const uploadImageKey = 'UploadImage'

// const userIdUrlParameterName = "userid"
const appIdUrlParameterName = 'appinstanceid'

export type json = any

export interface MetadataPartJSON {
  id: string
  objectType: string
  displayName: string
}

export interface Actor extends MetadataPartJSON {}

export interface Target extends MetadataPartJSON {
  /**
   * The forApplication property is in configuration resources,
   * it contains the name of the application for which the configuration is usable
   */
  forApplication?: string
}

export interface Generator extends MetadataPartJSON {
  url: string
}

export interface Provider extends MetadataPartJSON {
  url: string
  inquiryPhase: string
  inquiryPhaseId: string
  inquiryPhaseName: string
  ilsHasAngeLA: boolean
  ilsHasAngeLO?: boolean
}

export interface AppInfo {
  displayName: string
  id: string
  appType: string
  itemType: string
  url: string
  configuration?: ResourceJSON
  hidden?: boolean
}

export interface PhaseStructure {
  displayName: string
  id: string
  type: string
  visibilityLevel: string
  apps: AppInfo[]
  hidden?: boolean
}

export interface ILSStructure {
  displayName: string
  id: string
  url: string
  ilsId: string
  apps: AppInfo[]
  phases: PhaseStructure[]

  ilsHasAngeLA?: boolean
  offlineSupport?: boolean
}

export interface NextlabChannelMessage {
  type: string
  message: json
}

export type NextlabChannelMessageListener = (channelMessage: NextlabChannelMessage) => void

export interface NextlabChannelInitialisationData {
  actor: Actor
  generator: Generator
  provider: Provider
  ilsStructure: json
  users: Actor[]
  students: Student[]
  groups: GroupsInfo
}

/**
 * This interface specifies the methods of any storageHandler
 */
export interface StorageHandlerInterface {
  /**
   * created a new resource in the storage
   *
   * it will assing a new unique id to the resource and place it metadata.target.id of the returned resource
   *
   * @param {nextlab.storage.ResourceJSON} resource
   * @param {(error: any, resource: nextlab.storage.ResourceJSON) => void} callback
   */
  createResource: (resource: ResourceJSON, callback: (error: any, resource: ResourceJSON) => void) => void
  /**
   * created a new resource in the storage
   *
   * it will assing a new unique id to the resource and place it metadata.target.id of the returned resource
   *
   * @param {nextlab.storage.ResourceJSON} resource
   * @returns {Promise<nextlab.storage.ResourceJSON>}
   */
  createResourcePromise: (resource: ResourceJSON) => Promise<ResourceJSON>
  /**
   * returns the resource with the given id
   * if the resource cannot be found an error is returned
   *
   * @param {string} resourceId
   * @param {(error: any, resource: nextlab.storage.ResourceJSON) => void} callback
   */
  readResource: (resourceId: string, callback: (error: any, resource: ResourceJSON) => void) => void
  /**
   * returns the resource with the given id using a promise
   * if the resource cannot be found an error is returned
   *
   * @param {string} resourceId
   * @returns {Promise<nextlab.storage.ResourceJSON>}
   */
  readResourcePromise: (resourceId: string) => Promise<ResourceJSON>
  // when updated, also the metadata *might* change (e.g. information on actor groups, or flags like read-only)
  /**
   * update the stored resource
   * the specified resource must a be a resource as returned by the createResource or readResource call
   *
   * in the specified resource, the content could be different,
   * but also some metadata fields (eg. flags) *might* be changed
   *
   * the returned resource has updated values of the published field(s)
   *
   * if async is false, then the update must be done synchronously, without waiting for any response from the server
   * async is generally false in the case of saving the last changes before an app is closed
   * this means there is most likely no time left to get a response from the server before the app is really closed
   *
   * @param {nextlab.storage.ResourceJSON} resource
   * @param {(error: any, resource: nextlab.storage.ResourceJSON) => void} callback
   * @param {boolean} async
   */
  updateResource: (
    resource: ResourceJSON,
    callback: (error: any, resource: ResourceJSON) => void,
    async?: boolean,
  ) => void
  /**
   * update the stored resource, the result is returned use a promise
   * the specified resource must a be a resource as returned by the createResource or readResource call
   *
   * in the specified resource, the content could be different,
   * but also some metadata fields (eg. flags) *might* be changed
   *
   * the returned resource has updated values of the published field(s)
   *
   * if async is false, then the update must be done synchronously, without waiting for any response from the server
   * async is generally false in the case of saving the last changes before an app is closed
   * this means there is most likely no time left to get a response from the server before the app is really closed
   *
   * @param {nextlab.storage.ResourceJSON} resource
   * @param {boolean} async
   * @returns {Promise<nextlab.storage.ResourceJSON>}
   */
  updateResourcePromise: (resource: ResourceJSON, async?: boolean) => Promise<ResourceJSON>
  /**
   * deletes the resource with the given resourceId from the storage
   *
   * @param {string} resourceId
   * @param {(error: any) => void} callback
   */
  deleteResource: (resourceId: string, callback: (error: any) => void) => void
  /**
   * deletes the resource with the given resourceId from the storage
   * the promise is finished when the resource is deleted
   *
   * @param {string} resourceId
   * @returns {Promise<void>}
   */
  deleteResourcePromise: (resourceId: string) => Promise<void>
  /**
   * checks if a resource does exists
   *
   * @param {string} resourceId
   * @param {(error: any, exists: boolean) => void} callback
   */
  resourceExists: (resourceId: string, callback: (error: any, exists: boolean) => void) => void
  /**
   * checks if a resource does exists, returning the result using a promise
   *
   * @param {string} resourceId
   * @returns {Promise<boolean>}
   */
  resourceExistsPromise: (resourceId: string) => Promise<boolean>
  /**
   * returns all resources matching the query
   *
   * @param {nextlab.storage.ResourceQueryJSON} query
   * @param {(error: any, resources: nextlab.storage.ResourceJSON[]) => void} callback
   */
  queryResources: (
    query: ResourceQueryJSON,
    callback: (error: any, resources: ResourceJSON[]) => void,
    allowDirectCallback?: boolean,
  ) => void
  /**
   * returns all resources matching the query using a promise
   *
   * @param {nextlab.storage.ResourceQueryJSON} query
   * @param {boolean} allowDirectCallback
   * @returns {Promise<nextlab.storage.ResourceJSON[]>}
   */
  queryResourcesPromise: (query: ResourceQueryJSON, allowDirectCallback?: boolean) => Promise<ResourceJSON[]>

  /**
   * return a debug label, contains mostly the class name of the storage handler
   *
   * @returns {string}
   */
  getDebugLabel(): string

  /**
   * disable cache
   *
   */
  disableCache(): void

  /**
   * Set the disable state of the cache
   *
   * if there is no cache available it will do nothing
   *
   * @param {boolean} disabled
   */
  setDisableCache(disabled: boolean): void

  /**
   * Returns the disable state of the cache
   *
   * @returns {boolean}
   */
  getDisableCache(): boolean
}

interface StorageRequest {
  request: ChannelMessageStorageRequest
  callback: any
}

export interface ChannelMessageStorageRequest {
  id: string
  call: 'createResource' | 'readResource' | 'updateResource' | 'deleteResource' | 'resourceExists' | 'queryResources'
  parameter: ResourceJSON | string | ResourceQueryJSON
  async?: boolean
  fetchFromServer?: boolean
}

export interface ChannelMessageStorageAnswer {
  id: string
  error: string
  answer: ResourceJSON | boolean | ResourceJSON[]
}

export interface ChannelMessageIframeResizeRequest {
  desiredHeight: number
}

export interface ChannelMessageCallChatGptRequest {
  id: string
  model: string
  systemRole?: string
  temperature?: number
  userQuestion: string
}

interface RoleMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

export interface CallLlmParameters {
  provider: string
  model: string
  // systemRole?: string
  temperature?: number
  prompt: string
  messages?: Array<RoleMessage>
}

export interface GetIlsContentParameters {
  upToGeneratorId?: string
}

export interface ChannelMessageLlmManagerRequest {
  id: string
  call: 'listProviders' | 'callLlm' | 'getIlsContent'
  parameter?: CallLlmParameters | GetIlsContentParameters
}

export interface ChannelMessageLlmManagerAnswer {
  id: string
  error: string
  answer: string | LlmProviderInfo[]
}

export interface ChannelMessageCallLlmRequest {
  id: string
  provider: string
  model: string
  systemRole?: string
  temperature?: number
  prompt: string
  messages?: Array<RoleMessage>
}

export interface ChannelMessageCallChatGptAnswer {
  id: string
  error: string
  answer: string
}

export interface ChannelMessageUploadImageRequest {
  id: string
  imageData: string
}

export interface ChannelMessageUploadImageAnswer {
  id: string
  error: string
  answer: string
}

/**
 * a type definition for number values who are integers
 */
export type integer = number
/**
 * a type definition of number values who are positive integers (thus starting with 1)
 */
export type nth1 = number

/**
 * Describes the possible properties of resource query
 */
export interface ResourceQueryJSON {
  /**
   * only return resource who's metadata.actor.id is equal to the specified actorId
   *
   * if the actorId is not specified, then the resources are not filtered on metadata.actor.id
   */
  actorId?: string
  /**
   * only return resource who's metadata.generator.id is equal to the specified appId
   *
   * if the appId is not specified, then the resources are not filtered on metadata.generator.id
   */
  appId?: string
  /**
   * only return resource who's metadata.target.objectType is equal to the specified resourceType
   *
   * if the resourceType is not specified, then the resources are not filtered on metadata.target.objectType
   */
  resourceType?: string
  /**
   * if true returning the resource ordered on modification date, newest first
   *
   * if false or not specified, the implementation is free to select its own ordering or no ordering at all
   * it is probably a good idea to select whatever is faster at runtime
   */
  orderByModificationDate?: boolean
  /**
   * if true return only the metadata of the resources, thus the returned resources have no content properties
   *
   * if false or not specified, complete resources should be returned
   */
  returnMetadataOnly?: boolean // only return the metadata, not the content
  /**
   * part of the pagination options
   *
   * if specified, the results are grouped in pages, where maxResults is the number of results in a page
   * the pageNumber property specifies which page of results should be returned
   *
   * if not specified, return all resources matching the query
   */
  maxResults?: integer
  /**
   * part of the pagination options
   *
   * this property is only used, if the maxResults is also specified
   *
   * specifies which page of the results should be returned
   * the first page has page number 1
   */
  pageNumber?: nth1
  /**
   * only return resources who's metadata.publishedServer is at or after this ISO 8601 date
   *
   * if not specified, the resources are not filtered on metadata.publishedServer
   * implementations that cannot filter on it may return a superset of the matching resources
   */
  modifiedSince?: string
}

export enum Context {
  graasp = 'graasp',
  ils = 'ils',
  preview = 'preview',
  standalone = 'standalone',
  unknown = 'unknown',
}

/**
 * Describes the metadata part of the resource json.
 * It is based on the open social metadata
 */
export interface MetadataJSON {
  published?: string
  publishedServer?: string
  storageId?: string
  actor: Actor
  contextualActor?: Actor // the simulated actor in review mode
  target: json
  generator: Generator
  provider: Provider
  flags?: any
}

export interface ResourceJSON {
  metadata: MetadataJSON
  content?: json
}

export interface NextlabChannelMessageStorageRequest {
  id: string
  call: 'createResource' | 'readResource' | 'updateResource' | 'deleteResource' | 'resourceExists' | 'queryResources'
  parameter: ResourceJSON | string | ResourceQueryJSON
  async?: boolean
}

export interface NextlabChannelMessageStorageAnswer {
  id: string
  error: string
  answer: ResourceJSON | boolean | ResourceJSON[]
}

export interface ActivityStreamObject {
  published: string
  actor: Actor
  verb: string
  object: {
    objectType: string
    id?: string
    content?: any
  }
  target: Target
  generator: Generator
  provider: Provider
  appSequenceNumber: number
  sessionSequenceNumber?: number
  publishedServer?: string
}

export enum logActionVerbs {
  // content-oriented verbs
  // indicate that the content/model of a tool has changed
  add = 'add',
  remove = 'remove',
  change = 'change',
  clear = 'clear',

  // process-oriented verbs
  // no change to the content/model of a tool has been made,
  // but still the user has made a meaningful action
  access = 'access',
  cancel = 'cancel',
  start = 'start',
  stop = 'stop',
  send = 'send',
  receive = 'receive',

  // storage-oriented verbs
  // the user has stored or retrieved something, or created a new resource from scratch
  new = 'new',
  open = 'open',
  create = 'create',
  update = 'update',
  delete = 'delete',

  // used to indicate the start of a tool, lab, ils, etc.
  // the object specifies 'what' has been started
  // - in the case of a tool or lab, the object should contain metadata.generator
  // - in the case of an ILS, the object should contain metadata.provider
  application_started = 'access',
  application_stopped = 'cancel',
  phase_changed = 'access',
}

export interface RetrievedActivityStreamObject extends ActivityStreamObject {
  publishedServer: string
}

export interface LogActionQueryJSON {
  actor?: {
    id?: string | string[]
    // displayName?: string | string[],
    objectType?: string | string[]
  }
  target?: {
    objectType?: string | string[]
  }
  generator?: {
    id?: string | string[]
    // displayName?: string | string[],
    // objectType?: string | string[]
  }
  provider?: {
    inquiryPhaseId?: string | string[]
    // inquiryPhaseName?: string | string[]
  }
  object?: {
    objectType?: string | string[]
  }
  verb?: string | string[]
  published?: TimeSpecification
  publishedServer?: TimeSpecification
  /**
   * part of the pagination options
   *
   * if specified, the results are grouped in pages, where maxResults is the number of results in a page
   * the pageNumber property specifies which page of results should be returned
   *
   * if not specified, return all resources matching the query
   */
  maxResults?: integer
  /**
   * part of the pagination options
   *
   * this property is only used, if the maxResults is also specified
   *
   * specifies which page of the results should be returned
   * the first page has page number 1
   */
  pageNumber?: nth1
}

export interface TimeSpecification {
  start?: string
  end?: string
}

export interface LogActionProjectionJSON {
  actor?:
    | true
    | {
        id?: true
        displayName?: true
        objectType?: true
      }
  target?:
    | true
    | {
        id?: true
        displayName?: true
        objectType?: true
      }
  generator?:
    | true
    | {
        id?: true
        displayName?: true
        objectType?: true
        url?: true
      }
  provider?:
    | true
    | {
        id?: true
        displayName?: true
        objectType?: true
        url?: true
        inquiryPhase?: true
        inquiryPhaseId?: true
        inquiryPhaseName?: true
        ilsHasAngeLA?: true
        ilsHasAngeLO?: true
      }
  object?:
    | true
    | {
        id?: true
        content?: any
        objectType?: true
      }
  verb?: true
  appSequenceNumber?: true
  sessionSequenceNumber?: true
  published?: true
  publishedServer?: true
}

export interface NextlabChannelMessageLogAccessorRequest {
  id: string
  call: 'retrieveLogActions'
  parameter: {
    query: LogActionQueryJSON
    projection: LogActionProjectionJSON
  }
}

export interface NextlabChannelMessageLogAccessorAnswer {
  id: string
  error: string
  answer: LogAction[]
}

export interface ChannelMessageUserManagerRequest {
  id: string
  call: 'createUser' | 'retrieveUsers'
  parameter?: string
}

export interface ChannelMessageUserManagerAnswer {
  id: string
  error: string
  answer: string | Actor[]
}
